import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { FileTextIcon, GlobeIcon, InfoIcon, PaperclipIcon, UploadIcon } from 'lucide-react';
import { useState } from 'react';
import { ApprovalRequestStatus } from '../../types/enums/ApprovalRequestStatus.enum';
import { ResourceScope } from '../../types/enums/ResourceScope.enum';
import { ResourceType } from '../../types/enums/ResourceType.enum';
import { Resource } from '../../types/Resources/Resource.type';
import { ResourceCreate } from '../../types/Resources/ResourceCreate.type';
import { Statute } from '../../types/Statute.type';
import { User } from '../../types/User/User.type.ts';
import { getResourceReviewAlertSeverity } from '../../utils/resourceReviewAlertSeverity';
import ExternalLinkWarning from '../ExternalLinkWarning/ExternalLinkWarning';
import StatuteAutocomplete from '../StatuteAutocomplete/StatuteAutocomplete';
import { ResourceDocumentPreview } from './ResourceDocumentPreview';

const SCOPE_OPTIONS = [
  { id: ResourceScope.MYSELF, name: 'Myself only (Private)' },
  { id: ResourceScope.MYTEAM, name: 'My Team' },
  { id: ResourceScope.GLOBAL, name: 'Everyone' },
];

interface ResourceModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (resource: ResourceCreate, hasChanges: boolean) => void;
  onApprove?: () => void;
  onDecline?: () => void;
  resource?: Resource | null;
  mode: 'add' | 'edit';
  modalAction?: 'save' | 'approve' | 'decline';
  userInfo?: User;
  isLoading?: boolean;
  disabled?: boolean;
}

export function ResourceModal({
  open,
  onClose,
  onSave,
  onApprove,
  onDecline,
  resource,
  mode,
  modalAction = 'save',
  userInfo: _userInfo,
  isLoading = false,
  disabled = false,
}: ResourceModalProps) {
  const initEdit = resource && mode === 'edit';
  const [activeTab, setActiveTab] = useState<'attachment' | 'link'>(
    initEdit && resource.type.id === ResourceType.LINK ? 'link' : 'attachment',
  );
  const [title, setTitle] = useState(initEdit ? resource.name || '' : '');
  const [link, setLink] = useState(initEdit ? resource.link || '' : '');
  const [description, setDescription] = useState(initEdit ? resource.description || '' : '');
  const [statutes, setStatutes] = useState<Statute[]>(initEdit ? resource.statutes || [] : []);
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState(initEdit && resource.download_url ? resource.name || 'Current file' : '');
  const [scopeId, setScopeId] = useState<number>(
    initEdit && resource.scope.id ? resource.scope.id : ResourceScope.MYTEAM,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [fileDeleted, setFileDeleted] = useState(false);
  const [linkWarningOpen, setLinkWarningOpen] = useState(false);
  const [pendingLink, setPendingLink] = useState('');

  const [prevResource, setPrevResource] = useState(resource);
  const [prevMode, setPrevMode] = useState(mode);
  if (resource !== prevResource || mode !== prevMode) {
    setPrevResource(resource);
    setPrevMode(mode);

    if (resource && mode === 'edit') {
      setActiveTab(resource.type.id === ResourceType.LINK ? 'link' : 'attachment');
      setTitle(resource.name || '');
      setLink(resource.link || '');
      setDescription(resource.description || '');
      setStatutes(resource.statutes || []);
      setScopeId(resource.scope?.id ?? SCOPE_OPTIONS[0].id);
      if (resource.download_url) {
        setFileName(resource.name || 'Current file');
      }
      setFileDeleted(false);
    } else if (mode === 'add') {
      setActiveTab('attachment');
      setTitle('');
      setLink('');
      setDescription('');
      setStatutes([]);
      setFile(null);
      setFileName('');
      setScopeId(SCOPE_OPTIONS[0].id);
      setFileDeleted(false);
    }
  }

  const handleVisitClick = () => {
    if (link) {
      setPendingLink(link);
      setLinkWarningOpen(true);
    }
  };

  const handleConfirmLink = () => {
    window.open(pendingLink, '_blank');
    setLinkWarningOpen(false);
    setPendingLink('');
  };

  const handleCancelLink = () => {
    setLinkWarningOpen(false);
    setPendingLink('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 50 * 1024 * 1024) {
        alert('File size exceeds 50MB limit');
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      if (droppedFile.size > 50 * 1024 * 1024) {
        alert('File size exceeds 50MB limit');
        return;
      }
      setFile(droppedFile);
      setFileName(droppedFile.name);
    }
  };

  const hasChanges = () => {
    if (mode === 'add') return true;
    if (!resource) return false;

    if (title !== (resource.name || '')) return true;
    if (description !== (resource.description || '')) return true;
    if (activeTab === 'link' && link !== (resource.link || '')) return true;
    if (scopeId !== (resource.scope?.id ?? SCOPE_OPTIONS[0].id)) return true;

    if (file !== null) return true;
    if (fileDeleted) return true;

    const currentStatuteIds = statutes.map((s) => s.id).sort();
    const originalStatuteIds = (resource.statutes || []).map((s) => s.id).sort();
    if (JSON.stringify(currentStatuteIds) !== JSON.stringify(originalStatuteIds)) return true;

    return false;
  };

  const handleSave = () => {
    const type_id = activeTab === 'attachment' ? -1 : -2;

    const resourceData: ResourceCreate = {
      name: title,
      description: description,
      type_id: type_id,
      statutes: statutes.map((s) => s.id),
      resource_page_only: statutes.length === 0,
      scope_id: scopeId,
    };

    if (type_id === -1 && file) {
      resourceData.file = file;
    } else if (type_id === -2 && link) {
      resourceData.link = link;
    }

    onSave(resourceData, hasChanges());
    handleClose();
  };

  const handleClose = () => {
    setTitle('');
    setLink('');
    setDescription('');
    setStatutes([]);
    setFile(null);
    setFileName('');
    setScopeId(ResourceScope.MYTEAM);
    setActiveTab('attachment');
    setIsDragging(false);
    setFileDeleted(false);
    onClose();
  };

  const isFormValid = () => {
    if (!title.trim()) return false;
    if (!description.trim()) return false;
    if (activeTab === 'link' && !link.trim()) return false;
    if (activeTab === 'attachment' && mode === 'add' && !file) return false;
    if (activeTab === 'attachment' && mode === 'edit' && !file && !fileName) return false;
    return true;
  };

  const pendingReview =
    resource?.approval_requests?.find((req) => req.status.id === ApprovalRequestStatus.PENDING) ||
    resource?.approval_requests?.[resource.approval_requests.length - 1];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogContent sx={{ paddingTop: 3 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
              Title
            </Typography>
            <TextField
              fullWidth
              placeholder='Enter resource title'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size='small'
              disabled={disabled}
              sx={{ marginBottom: 2 }}
            />

            {/* Tab Selection */}
            <Tabs
              value={activeTab}
              onChange={(_event, newValue) => {
                if (mode === 'add' && !disabled) {
                  setActiveTab(newValue);
                }
              }}
              sx={{
                marginBottom: 2,
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: '48px',
                '& .MuiTab-root': {
                  minHeight: '48px',
                  textTransform: 'none',
                  opacity: mode === 'edit' ? 0.5 : 1,
                  cursor: mode === 'edit' ? 'not-allowed' : 'pointer',
                },
              }}
            >
              <Tab
                value='attachment'
                icon={<PaperclipIcon size={16} />}
                iconPosition='start'
                label='Attachment'
                disabled={mode === 'edit' && activeTab !== 'attachment'}
              />
              <Tab
                value='link'
                icon={<GlobeIcon size={16} />}
                iconPosition='start'
                label='Link'
                disabled={mode === 'edit' && activeTab !== 'link'}
              />
            </Tabs>

            {/* Attachment Tab */}
            {activeTab === 'attachment' && (
              <>
                {mode === 'edit' && resource?.download_url && !file && !fileDeleted ? (
                  <Box
                    sx={{
                      marginBottom: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <FileTextIcon size={24} color='#132B50' />
                    <Typography variant='body2' sx={{ flex: 1 }}>
                      {fileName}
                    </Typography>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => setPreviewModalOpen(true)}
                      sx={{ textTransform: 'none', minWidth: '80px' }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      size='small'
                      onClick={() => {
                        setFileDeleted(true);
                        setFile(null);
                        setFileName('');
                      }}
                      sx={{ textTransform: 'none', minWidth: '80px' }}
                      disabled={disabled}
                    >
                      Delete
                    </Button>
                  </Box>
                ) : (
                  <Box
                    component='div'
                    role='presentation'
                    onDragOver={!disabled ? handleDragOver : undefined}
                    onDragLeave={!disabled ? handleDragLeave : undefined}
                    onDrop={!disabled ? handleDrop : undefined}
                    sx={{
                      border: '2px dashed #cbd5e0',
                      borderRadius: 2,
                      padding: 4,
                      textAlign: 'center',
                      marginBottom: 2,
                      backgroundColor: disabled ? '#f5f5f5' : isDragging ? '#e6f2ff' : '#f7fafc',
                      borderColor: isDragging ? '#3182ce' : '#cbd5e0',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: disabled ? 0.6 : 1,
                    }}
                  >
                    <input
                      accept='.doc,.docx,.pdf'
                      id='file-upload'
                      type='file'
                      onChange={handleFileChange}
                      disabled={disabled}
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor={!disabled ? 'file-upload' : undefined}
                      style={{
                        display: 'block',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        width: '100%',
                      }}
                    >
                      <UploadIcon size={24} color='#718096' />
                      <Typography variant='body2'>
                        {fileName || (
                          <>
                            Drop here to attach or{' '}
                            <Typography
                              component='span'
                              variant='body2'
                              sx={{
                                color: 'accentBlue.main',
                                fontWeight: 600,
                                textDecoration: 'underline',
                              }}
                            >
                              upload
                            </Typography>
                          </>
                        )}
                      </Typography>
                    </label>
                    <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', marginTop: 1 }}>
                      Max size: 50MB
                      <br />
                      Accepted formats: .doc, .docx, .pdf
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Link Tab */}
            {activeTab === 'link' && (
              <Box sx={{ marginBottom: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  fullWidth
                  placeholder='https://example.com/'
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  size='small'
                  disabled={disabled}
                />
                {mode === 'edit' && link && (
                  <Button variant='outlined' size='small' onClick={handleVisitClick} sx={{ textTransform: 'none' }}>
                    Visit
                  </Button>
                )}
              </Box>
            )}

            {/* General Access */}
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
              Who can access?
            </Typography>
            <TextField
              select
              fullWidth
              size='small'
              value={scopeId}
              onChange={(event) => setScopeId(Number(event.target.value))}
              disabled={disabled}
              slotProps={{
                select: {
                  inputProps: {
                    'aria-label': 'Select access',
                  },
                },
              }}
              sx={{ marginBottom: 2, '& .MuiInputLabel-root': { fontSize: 14 } }}
            >
              {SCOPE_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Description */}
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
              Description
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder='Enter description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size='small'
              disabled={disabled}
              sx={{ marginBottom: 2 }}
            />

            {/* Statutes */}
            <Box sx={{ marginBottom: 2, width: '100%' }}>
              <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
                Statutes
              </Typography>
              <StatuteAutocomplete value={statutes} onChange={setStatutes} isEnhancement={false} disabled={disabled} />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{ padding: 3, display: modalAction === 'save' ? 'block' : 'flex', flexDirection: 'column', gap: 2 }}
      >
        {modalAction === 'approve' || modalAction === 'decline' ? (
          <>
            {pendingReview && (
              <Alert
                severity={getResourceReviewAlertSeverity(pendingReview.action_type.id)}
                icon={<InfoIcon size={20} />}
                sx={{
                  width: '100%',
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{pendingReview.action_type.name} Request</Typography>
                <Typography sx={{ fontSize: 14 }}>
                  You are reviewing a {pendingReview.action_type.description}.
                </Typography>
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', width: '100%' }}>
              <Button
                onClick={() => {
                  if (onDecline) onDecline();
                }}
                variant='outlined'
                color='error'
                sx={{ textTransform: 'none', minWidth: 100 }}
              >
                Decline
              </Button>
              <Button
                onClick={() => {
                  if (onApprove) onApprove();
                }}
                variant='contained'
                color='success'
                disableElevation
                sx={{ textTransform: 'none', minWidth: 140, color: '#fff' }}
              >
                Approve
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={handleClose} color='accentBlue' sx={{ textTransform: 'none', minWidth: 100 }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant='contained'
              color='primary'
              disabled={!isFormValid() || (mode === 'edit' && !hasChanges())}
              disableElevation
              sx={{ textTransform: 'none', minWidth: 140, color: '#fff' }}
            >
              {mode === 'add' ? 'Save Resource' : 'Update Resource'}
            </Button>
          </Box>
        )}
      </DialogActions>

      <ResourceDocumentPreview
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        documentUrl={resource?.download_url}
      />
      <ExternalLinkWarning
        open={linkWarningOpen}
        url={pendingLink}
        onConfirm={handleConfirmLink}
        onCancel={handleCancelLink}
      />
    </Dialog>
  );
}

export default ResourceModal;
